import { useCallback } from 'react';

/**
 * クイズデータの検証を担当する共通フック
 * 既存のuseQuizPlayer.jsとQuizSection.jsから検証ロジックを抽出・統一
 */
const useQuizValidation = () => {
  
  /**
   * Firebase保存用データの検証とクリーニング
   * undefinedやnullの値を適切に処理し、Firebaseで保存可能な形式に変換
   * @param {any} obj - 検証・クリーニング対象のオブジェクト
   * @returns {any} - クリーニング済みのオブジェクト
   */
  const validateFirebaseData = useCallback((obj) => {
    if (obj === null || obj === undefined) {
      return null;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(validateFirebaseData);
    }
    
    if (typeof obj === 'object') {
      const cleaned = {};
      Object.keys(obj).forEach(key => {
        const value = validateFirebaseData(obj[key]);
        if (value !== undefined) {
          cleaned[key] = value;
        }
      });
      return cleaned;
    }
    
    return obj === undefined ? null : obj;
  }, []);

  /**
   * クイズ回答データの検証
   * userAnswerがundefinedまたはnullの場合、空文字列に変換
   * @param {Object} response - 回答オブジェクト
   * @returns {Object} - 検証済み回答オブジェクト
   */
  const validateQuizResponse = useCallback((response) => {
    if (!response) {
      return {
        isCorrect: false,
        explanation: "",
        userAnswer: "",
        correctAnswer: "",
        type: "",
        details: {}
      };
    }

    return {
      isCorrect: response?.isCorrect || false,
      explanation: response?.explanation || "",
      userAnswer: response?.userAnswer || "",
      correctAnswer: response?.correctAnswer || "",
      type: response?.type || "",
      details: response?.details || {}
    };
  }, []);

  /**
   * 問題タイプ別の回答データ検証
   * @param {Object} question - 問題オブジェクト
   * @param {Object} response - 回答オブジェクト
   * @returns {Object} - 問題タイプに応じた検証済み回答オブジェクト
   */
  const validateQuestionTypeResponse = useCallback((question, response) => {
    if (!question || !response) {
      return null;
    }

    const safeResponse = validateQuizResponse(response);
    
    const baseDetails = {
      questionId: question.id,
      questionType: question.type,
      isCorrect: safeResponse.isCorrect,
      explanation: safeResponse.explanation,
    };

    switch (question.type) {
      case 'multiple-choice':
        return {
          ...baseDetails,
          question: question.question,
          choices: question.choices,
          correctAnswer: question.answer,
          userAnswer: safeResponse.userAnswer,
          details: {
            type: question.type,
            choices: question.choices,
            answer: question.answer,
            userResponse: safeResponse
          }
        };

      case 'sentence-rearrangement':
        return {
          ...baseDetails,
          question: question.japaneseTranslation,
          englishSentence: question.englishSentence,
          japaneseTranslation: question.japaneseTranslation,
          userAnswer: safeResponse.userAnswer,
          details: {
            type: question.type,
            englishSentence: question.englishSentence,
            japaneseTranslation: question.japaneseTranslation,
            userResponse: safeResponse
          }
        };

      case 'true-false':
        return {
          ...baseDetails,
          question: question.question,
          correctAnswer: question.answer,
          userAnswer: safeResponse.userAnswer,
          details: {
            type: question.type,
            answer: question.answer,
            userResponse: safeResponse
          }
        };

      case 'fill-in-the-blank':
        return {
          ...baseDetails,
          question: question.question,
          correctAnswer: question.answer,
          userAnswer: safeResponse.userAnswer,
          details: {
            type: question.type,
            answer: question.answer,
            userResponse: safeResponse
          }
        };

      case 'matching':
        return {
          ...baseDetails,
          question: question.question,
          leftItems: question.leftItems,
          rightItems: question.rightItems,
          correctMatches: question.correctMatches,
          userAnswer: safeResponse.userAnswer,
          details: {
            type: question.type,
            leftItems: question.leftItems,
            rightItems: question.rightItems,
            correctMatches: question.correctMatches,
            userResponse: safeResponse
          }
        };

      default:
        return null;
    }
  }, [validateQuizResponse]);

  /**
   * 詳細結果配列の検証とクリーニング
   * @param {Array} questions - 問題配列
   * @param {Object} responses - 回答オブジェクト
   * @returns {Array} - 検証済み詳細結果配列
   */
  const validateDetailedResults = useCallback((questions, responses) => {
    if (!questions || !Array.isArray(questions)) {
      return [];
    }

    const detailedResults = questions.map(question => {
      const response = responses[question.id] || {
        isCorrect: false,
        explanation: "時間切れまたは未回答",
        userAnswer: "",
        type: question.type,
        details: question
      };

      return validateQuestionTypeResponse(question, response);
    }).filter(Boolean); // nullを除外

    return detailedResults;
  }, [validateQuestionTypeResponse]);

  return {
    validateFirebaseData,
    validateQuizResponse,
    validateQuestionTypeResponse,
    validateDetailedResults
  };
};

export { useQuizValidation };
export default useQuizValidation; 